import type { ProcessingType } from './index';

export interface PaymentData {
  email: string;
  firstName: string;
  lastName: string;
  companyName: string;
  agreeToTerms: boolean;
  fcraAcknowledgment: boolean;
}

export interface StripePaymentData {
  paymentIntentId: string;
  clientSecret: string;
}

export interface ProcessingJob {
  jobId: string;
  file?: File;
  fileName?: string;
  processingType: ProcessingType;
  processableRows: number;
  totalRows: number;
  estimatedCost: number;
  maxPossibleCost: number;
  s3Key: string;
  s3Bucket: string;
  userId?: string;
  companyName?: string;
  aiOverview?: boolean;
  savePaymentMethod?: boolean;
}

export interface NavigationState extends Partial<ProcessingJob> {
  rowCount?: number;
  invalidRows?: number;
}

export interface AccountInviteStatus {
  invited: boolean;
  requiresConfirmation?: boolean;
  message?: string;
  error?: string;
}

export const PROCESSING_LABELS: Record<ProcessingType, string> = {
  validate: 'Validate',
  enrich: 'Enrich',
  enrich_validate: 'Enrich + Validate',
  scoring: 'Scoring',
  scoring_enrich: 'Scoring + Enrich',
};

/**
 * Resolve processing rate based on row count using tiered pricing
 * Tier 1: 5-24 rows (small batch premium)
 * Tier 2: 25-499 rows (standard rate)
 * Tier 3: 500+ rows (volume discount)
 *
 * @param type - Processing type
 * @param rowCount - Number of rows to process
 * @param aiOverview - Whether AI overview is enabled (adds $0.03/record)
 * @returns Per-row rate for the given processing type and row count
 */
export const resolveProcessingRate = (
  type: ProcessingType,
  rowCount: number,
  aiOverview = false
): number => {
  // Tiered pricing structure (matching BATCH_PRICING in config/pricing.ts)
  const BATCH_PRICING = {
    validate: {
      tier1: 0.18, // 5-24 rows
      tier2: 0.15, // 25-499 rows
      tier3: 0.12, // 500+ rows
    },
    enrich: {
      tier1: 0.04,
      tier2: 0.03,
      tier3: 0.025,
    },
    enrich_validate: {
      tier1: 0.20,
      tier2: 0.18,
      tier3: 0.15,
    },
    scoring: {
      tier1: 0.55,
      tier2: 0.48,
      tier3: 0.42,
    },
    scoring_enrich: {
      tier1: 0.60,
      tier2: 0.52,
      tier3: 0.45,
    },
  };

  // Determine tier based on row count
  let baseRate: number;
  if (rowCount < 25) {
    baseRate = BATCH_PRICING[type].tier1;
  } else if (rowCount < 500) {
    baseRate = BATCH_PRICING[type].tier2;
  } else {
    baseRate = BATCH_PRICING[type].tier3;
  }

  // Add AI Overview cost if applicable
  if (aiOverview && (type === 'scoring' || type === 'scoring_enrich')) {
    return baseRate + 0.03; // AI overview add-on
  }

  return baseRate;
};

export const PAYMENT_STORAGE_KEY = (jobId: string) => `collectability-payment-${jobId}`;
